/**
 * The one piece of "capacity to onboard new customers" that was missing: a
 * way to actually call POST /api/customers. The endpoint (avrio-backend#129)
 * has existed and been deployed since 2026-09-24 with nothing in the
 * frontend calling it - a Super Admin's only way to onboard a new
 * enterprise customer was a raw API request.
 *
 * Super Admin only, matching CustomerService#createCustomer's
 * @PreAuthorize exactly - not offered to Admin at all, who has no
 * authority to create a customer in the first place.
 */

import { memo, useCallback, useState } from 'react';
import { Alert, Form, Input, Modal } from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerListItem } from '../../types';

export interface CreateCustomerModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired with the new customer after the API confirms it was created. */
  onCreated: (customer: CustomerListItem) => void;
}

interface FormValues {
  customerName: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}

const CreateCustomerModal = ({
  open,
  onClose,
  onCreated,
}: CreateCustomerModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    form.resetFields();
    setError(null);
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      setSubmitting(true);
      setError(null);
      axios
        .post<CustomerListItem>('/api/customers', values)
        .then((res) => {
          form.resetFields();
          onCreated(res.data);
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 409) {
            setError('An Avrio account already exists for that admin email.');
          } else if (status === 400) {
            setError(
              err?.response?.data?.message ??
                'Check the fields above and try again.',
            );
          } else {
            setError('Could not create the customer. Try again.');
          }
        })
        .finally(() => setSubmitting(false));
    },
    [form, onCreated],
  );

  return (
    <Modal
      title="Create a new customer"
      open={open}
      onCancel={handleClose}
      onOk={form.submit}
      okText="Create"
      confirmLoading={submitting}
      destroyOnClose
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="customerName"
          label="Customer name"
          rules={[{ required: true, message: 'A customer name is required' }]}
        >
          <Input placeholder="Acme Co" />
        </Form.Item>
        <Form.Item
          name="adminFirstName"
          label="First admin user - first name"
          rules={[{ required: true, message: 'A first name is required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="adminLastName"
          label="First admin user - last name"
          rules={[{ required: true, message: 'A last name is required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="adminEmail"
          label="First admin user - email"
          rules={[
            { required: true, message: 'An email is required' },
            { type: 'email', message: 'Enter a valid email address' },
          ]}
        >
          <Input placeholder="admin@customer.com" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(CreateCustomerModal);
